import { create } from 'zustand';
import type { AppState, Notification } from '../types';

interface AppStore extends AppState {
  // Actions
  setOnlineStatus: (isOnline: boolean) => void;
  setSyncStatus: (status: 'idle' | 'syncing' | 'error') => void;
  addNotification: (notification: Omit<Notification, 'id' | 'createdAt' | 'read'>) => void;
  markNotificationRead: (id: string) => void;
  clearNotifications: () => void;
  removeNotification: (id: string) => void;
  setCurrentModule: (module: 'picking' | 'packing' | 'analytics' | 'scanner') => void;
  toggleVoice: () => void;
  addOfflineData: (data: any) => void;
  clearOfflineData: () => void;
}

export const useAppStore = create<AppStore>((set, get) => ({
  isOnline: navigator.onLine,
  syncStatus: 'idle',
  notifications: [],
  currentModule: 'picking',
  voiceEnabled: false,
  offlineData: [],

  setOnlineStatus: (isOnline) => {
    set({ isOnline });
    
    // Add notification when going offline/online
    if (isOnline) {
      get().addNotification({
        type: 'success',
        title: 'Back Online',
        message: 'Connection restored. Syncing data...',
        priority: 'medium'
      });
      set({ syncStatus: 'syncing' });
      
      // Simulate sync completion
      setTimeout(() => {
        set({ syncStatus: 'idle' });
        get().clearOfflineData();
      }, 3000);
    } else {
      get().addNotification({
        type: 'warning',
        title: 'Offline Mode',
        message: 'Working offline. Data will sync when connection is restored.',
        priority: 'high'
      });
    }
  },

  setSyncStatus: (status) => set({ syncStatus: status }),

  addNotification: (notification) => {
    const newNotification: Notification = {
      ...notification,
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString(),
      read: false
    };
    
    set((state) => ({
      notifications: [newNotification, ...state.notifications]
    }));
    
    // Auto-remove informational notifications after 1.5 seconds
    if (notification.type === 'info' || 
        (notification.type === 'success' && notification.priority === 'low')) {
      setTimeout(() => {
        get().removeNotification(newNotification.id);
      }, 1500);
    }
    
    // Auto-remove other success notifications after 3 seconds
    else if (notification.type === 'success') {
      setTimeout(() => {
        get().removeNotification(newNotification.id);
      }, 3000);
    }
  },

  markNotificationRead: (id) => {
    set((state) => ({
      notifications: state.notifications.map(notification =>
        notification.id === id
          ? { ...notification, read: true }
          : notification
      )
    }));
  },

  clearNotifications: () => set({ notifications: [] }),

  removeNotification: (id) => {
    set((state) => ({
      notifications: state.notifications.filter(notification => notification.id !== id)
    }));
  },

  setCurrentModule: (module) => set({ currentModule: module }),

  toggleVoice: () => set((state) => ({ voiceEnabled: !state.voiceEnabled })),

  addOfflineData: (data) => {
    set((state) => ({
      offlineData: [...state.offlineData, { ...data, timestamp: Date.now() }]
    }));
  },

  clearOfflineData: () => set({ offlineData: [] })
}));

// Listen for online/offline events
window.addEventListener('online', () => {
  useAppStore.getState().setOnlineStatus(true);
});

window.addEventListener('offline', () => {
  useAppStore.getState().setOnlineStatus(false);
});